import type {
  AircraftObservation, AircraftState, AltitudeRef, CameraObservation, CameraState, CrewObservation, CrewState, EpochMs, Freshness,
  GeneralWeather, LatLon, MetarReport, MissionRecord, TafReport, TrailPoint, WeatherObservation, WorldState,
} from './types.ts';
import { angleDiffDeg, destinationPoint, distanceM, interpolateGreatCircle, M_PER_FT, MPS_PER_KT, normalizeDeg } from './geo.ts';
import { lightPhase, sunPosition } from './sun.ts';
import { cameraFov, focalRange, fovDeg, isZoom } from './fov.ts';
import { inferFlightPhase, PHASE_TREND_SEC, type PhaseResult } from './phase.ts';
import { atMostStale, isLowIntegrity, isReportedSource, LOCAL_ADSB_FRESHNESS, LOCAL_ADSB_SOURCE_PREFIX } from './altitude.ts';

export interface FreshnessThresholds {
  /** ≤ live → 'live'; ≤ aging → 'aging'; ≤ stale → 'stale'; else 'lost'. Seconds. */
  live: number;
  aging: number;
  stale: number;
}

/**
 * Field UX spec §5.2 / §6.
 * Aircraft: live ≤ 5 s, aging ≤ 15 s, stale ≤ 60 s, lost > 60 s. (A 15 s FlightAware poll reads
 * "aging" most of the time — that is the honest label.)
 * Crew (phones report every few seconds to a minute): live ≤ 15 s, aging ≤ 5 min, stale ≤ 30 min,
 * lost beyond — i.e. a crew position is "stale" once it is more than 5 min old (§6).
 */
export const AIRCRAFT_FRESHNESS: FreshnessThresholds = { live: 5, aging: 15, stale: 60 };
export const CREW_FRESHNESS: FreshnessThresholds = { live: 15, aging: 300, stale: 1800 };
/**
 * Dead-reckon an aircraft along its last track/speed for up to this long past its last sample; after
 * that it is held at the 60 s estimate (never snapped back to the old fix — that read as the aircraft
 * jumping backwards in time on the first real shoot). Backtest on N555VX over adsb.lol (2026-09-30,
 * gaps up to 51 s): straight-line dead reckoning missed the next fix by a median 39 m on 10–20 s gaps and
 * 213 m on 20–60 s gaps, vs 818 m / 1,773 m for holding the last fix (turn-aware prediction did worse).
 * Freshness still ages honestly (stale/lost) and the map shows the estimate hollow with a growing ring.
 */
export const MAX_EXTRAPOLATE_SEC = 60;
/** Display uncertainty of a dead-reckoned position: this fraction of the distance flown since the fix. */
export const ESTIMATE_ERROR_FRACTION = 0.15;

/**
 * Network aggregators (adsb.lol & co.) and FlightAware: adsb.lol allows ~1 request / 2–3 s per IP and
 * its fixes are already a few seconds old, so 5–15 s ages are normal operation, not a dropout.
 * live ≤ 10 s, aging ≤ 30 s, stale ≤ 90 s (aviation data spec §2: aggregator nominal 0.2–1 Hz).
 */
export const NETWORK_ADSB_FRESHNESS: FreshnessThresholds = { live: 10, aging: 30, stale: 90 };
const NETWORK_SOURCES = new Set(['adsb.lol', 'adsb.fi', 'airplanes.live', 'flightaware', 'public']);

/** Aircraft thresholds for a sample's source: local receiver 3 s / 30 s; network feeds 10/30/90 s; else §5.2. */
export function aircraftFreshnessFor(source: string | null | undefined): FreshnessThresholds {
  if (source?.startsWith(LOCAL_ADSB_SOURCE_PREFIX)) return LOCAL_ADSB_FRESHNESS;
  if (source && NETWORK_SOURCES.has(source)) return NETWORK_ADSB_FRESHNESS;
  return AIRCRAFT_FRESHNESS;
}

/** Null age → 'none'. Negative ages (clock skew) count as live. */
export function freshnessFor(ageSec: number | null, th: FreshnessThresholds): Freshness {
  if (ageSec == null || Number.isNaN(ageSec)) return 'none';
  if (ageSec <= th.live) return 'live';
  if (ageSec <= th.aging) return 'aging';
  if (ageSec <= th.stale) return 'stale';
  return 'lost';
}

export interface TrackOptions {
  /** Max gap between two samples that may be interpolated (s). Default 60. */
  maxInterpolateGapSec?: number;
  /** How far past the last sample to dead-reckon (s). Default 10 (MAX_EXTRAPOLATE_SEC); 0 disables. */
  maxExtrapolateSec?: number;
  /** Seconds of trail to include. Default 600. */
  trailSec?: number;
  /** Max trail points (downsampled evenly). Default 400. */
  trailMaxPoints?: number;
}

/** A sample within this distance of the query time labels the state 'observed'. */
export const OBSERVED_TOLERANCE_MS = 1500;
/** A camera observation's position is used for this long after it was taken. */
export const CAMERA_POSITION_MAX_AGE_SEC = 120;

/** Index of the last element with t ≤ query (binary search); −1 if none. Array must be sorted by t. */
export function lastIndexAtOrBefore<T extends { t: EpochMs }>(arr: readonly T[], t: EpochMs): number {
  let lo = 0;
  let hi = arr.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (arr[mid]!.t <= t) {
      ans = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return ans;
}

/** Evenly downsample a sorted series to at most `max` points, always keeping first & last. */
export function downsample<T>(arr: readonly T[], max: number): T[] {
  const n = arr.length;
  if (max <= 0 || n === 0) return [];
  if (n <= max) return arr.slice();
  if (max === 1) return [arr[n - 1]!];
  const out: T[] = [];
  const step = (n - 1) / (max - 1);
  for (let i = 0; i < max; i++) out.push(arr[Math.round(i * step)]!);
  return out;
}

const lerp = (a: number, b: number, f: number) => a + (b - a) * f;
const lerpN = (a: number | null, b: number | null, f: number): number | null =>
  a != null && b != null ? lerp(a, b, f) : f < 0.5 ? a : b;
const lerpAngle = (a: number | null, b: number | null, f: number): number | null =>
  a != null && b != null ? normalizeDeg(a + angleDiffDeg(a, b) * f) : f < 0.5 ? a : b;

type Mode = 'observed-exact' | 'interpolate' | 'extrapolate' | 'hold';

interface Located<O> {
  prev: O;
  next: O | null;
  i: number;
  mode: Mode;
  f: number;
  elapsedSec: number;
  nearSample: boolean;
}

function locate<O extends { t: EpochMs }>(
  obs: readonly O[],
  t: EpochMs,
  maxGapSec: number,
  maxExtrapSec: number,
  canExtrapolate: (o: O) => boolean,
): Located<O> | null {
  const i = lastIndexAtOrBefore(obs, t);
  if (i < 0) return null;
  const prev = obs[i]!;
  const next = i + 1 < obs.length ? obs[i + 1]! : null;
  const elapsedMs = t - prev.t;
  const nearSample = elapsedMs <= OBSERVED_TOLERANCE_MS || (next != null && next.t - t <= OBSERVED_TOLERANCE_MS && next.t - prev.t <= maxGapSec * 1000);
  if (elapsedMs === 0) return { prev, next, i, mode: 'observed-exact', f: 0, elapsedSec: 0, nearSample: true };
  if (next && next.t - prev.t <= maxGapSec * 1000) {
    return { prev, next, i, mode: 'interpolate', f: elapsedMs / (next.t - prev.t), elapsedSec: elapsedMs / 1000, nearSample };
  }
  // beyond maxExtrapSec the caller holds the capped estimate (it never snaps back to `prev`)
  if (maxExtrapSec > 0 && canExtrapolate(prev)) {
    return { prev, next, i, mode: 'extrapolate', f: 0, elapsedSec: elapsedMs / 1000, nearSample };
  }
  return { prev, next, i, mode: 'hold', f: 0, elapsedSec: elapsedMs / 1000, nearSample: elapsedMs <= OBSERVED_TOLERANCE_MS };
}

/**
 * Trail of real samples in (t − trailSec, t], plus the current position when it is not itself a
 * sample. Downsampling picks every k-th sample by ABSOLUTE index so the chosen points do not
 * shimmer as the window slides frame to frame.
 */
function buildTrail<O extends { t: EpochMs; lat: number; lon: number }>(
  obs: readonly O[],
  lastIdx: number,
  t: EpochMs,
  trailSec: number,
  maxPoints: number,
  altFt: (o: O) => number | null,
  current: TrailPoint | null,
): TrailPoint[] {
  if (trailSec <= 0 || maxPoints <= 0) return [];
  const startIdx = lastIndexAtOrBefore(obs, t - trailSec * 1000) + 1;
  const count = lastIdx - startIdx + 1;
  const budget = Math.max(2, maxPoints - (current ? 1 : 0));
  const stride = count > budget ? Math.ceil(count / (budget - 1)) : 1;
  const out: TrailPoint[] = [];
  for (let k = startIdx; k <= lastIdx; k++) {
    if (k !== startIdx && k !== lastIdx && k % stride !== 0) continue;
    const o = obs[k]!;
    out.push({ t: o.t, lat: o.lat, lon: o.lon, altFt: altFt(o) });
  }
  if (current && (out.length === 0 || out[out.length - 1]!.t < current.t)) out.push(current);
  return out.length > maxPoints ? downsample(out, maxPoints) : out;
}

const DEFAULT_TRACK: Required<TrackOptions> = { maxInterpolateGapSec: 60, maxExtrapolateSec: MAX_EXTRAPOLATE_SEC, trailSec: 600, trailMaxPoints: 400 };

/**
 * Aircraft state at `t` from its sorted observations.
 * - exact/near sample (|Δ| ≤ 1.5 s) → 'observed' (or the sample's own quality, e.g. 'manual')
 * - between samples within gap → great-circle + linear alt/speed, 'interpolated'
 * - after last sample with gs/track → dead-reckoned, 'estimated'; beyond maxExtrapolateSec held at the
 *   capped estimate (continuous — never jumps back to the last fix); `estimateM` = display uncertainty
 * - otherwise → last known position, quality of that sample, freshness from age (stale/lost)
 * Returns null if no sample at or before `t`.
 *
 * Position is always the best estimate (interpolated/dead-reckoned) so motion is smooth at 60 fps;
 * the 1.5 s tolerance only affects the quality label.
 */
export function aircraftStateAt(aircraftId: string, obs: readonly AircraftObservation[], t: EpochMs, opts?: TrackOptions): AircraftState | null {
  const o = { ...DEFAULT_TRACK, ...stripUndefined(opts) };
  // The dead-reckoning cap is a product rule: options can shorten it, never extend it.
  o.maxExtrapolateSec = Math.min(o.maxExtrapolateSec, MAX_EXTRAPOLATE_SEC);
  const loc = locate(obs, t, o.maxInterpolateGapSec, o.maxExtrapolateSec, (p) => p.gsKt != null && p.trackDeg != null);
  if (!loc) return null;
  const { prev, next } = loc;
  let lat = prev.lat;
  let lon = prev.lon;
  let altFt = prev.altFt;
  let gsKt = prev.gsKt;
  let trackDeg = prev.trackDeg;
  let vsFpm = prev.vsFpm;
  let quality = prev.quality;
  let estimateM = 0;

  if (loc.mode === 'interpolate' && next) {
    const p = interpolateGreatCircle(prev, next, loc.f);
    lat = p.lat;
    lon = p.lon;
    altFt = lerpN(prev.altFt, next.altFt, loc.f);
    gsKt = lerpN(prev.gsKt, next.gsKt, loc.f);
    trackDeg = lerpAngle(prev.trackDeg, next.trackDeg, loc.f);
    vsFpm = lerpN(prev.vsFpm, next.vsFpm, loc.f);
    quality = 'interpolated';
  } else if (loc.mode === 'extrapolate') {
    const dt = Math.min(loc.elapsedSec, o.maxExtrapolateSec);
    const flownM = prev.gsKt! * MPS_PER_KT * dt;
    const p = destinationPoint(prev, prev.trackDeg!, flownM);
    lat = p.lat;
    lon = p.lon;
    if (prev.altFt != null && prev.vsFpm != null) altFt = prev.altFt + (prev.vsFpm * dt) / 60;
    quality = 'estimated';
    estimateM = flownM * ESTIMATE_ERROR_FRACTION;
  }
  if (loc.nearSample) {
    const near = next && (next.t - t) < (t - prev.t) && loc.mode === 'interpolate' ? next : prev;
    quality = near.quality;
  }

  const ageSec = (t - prev.t) / 1000;
  const current: TrailPoint | null = loc.mode === 'observed-exact' ? null : { t, lat, lon, altFt };
  const trail = buildTrail(obs, loc.i, t, o.trailSec, o.trailMaxPoints, (x) => x.altFt, current);
  let freshness = freshnessFor(ageSec, aircraftFreshnessFor(prev.source));
  // A crew-reported position ("REPORTED by JAKE") is never shown as live.
  if (isReportedSource(prev.source)) freshness = atMostStale(freshness);
  const extra: Partial<AircraftState> = {};
  if (prev.accuracyM != null) extra.accuracyM = prev.accuracyM;
  if (prev.nacp != null || prev.accuracyM != null) extra.lowIntegrity = isLowIntegrity(prev);
  if (prev.altRef != null) extra.altRef = prev.altRef;
  if (estimateM > 0) extra.estimateM = estimateM;
  return {
    ...extra,
    aircraftId,
    t,
    lat,
    lon,
    altFt,
    gsKt,
    trackDeg: trackDeg != null ? normalizeDeg(trackDeg) : null,
    vsFpm,
    quality,
    lastObservedT: prev.t,
    ageSec,
    freshness,
    source: prev.source,
    trail,
  };
}

const DEFAULT_CREW_TRACK: Required<TrackOptions> = { maxInterpolateGapSec: 60, maxExtrapolateSec: 0, trailSec: 600, trailMaxPoints: 400 };

/** Same semantics for crew (no extrapolation by default; dead reckoning uses speedMps/headingDeg). */
export function crewStateAt(userId: string, obs: readonly CrewObservation[], t: EpochMs, opts?: TrackOptions): CrewState | null {
  const o = { ...DEFAULT_CREW_TRACK, ...stripUndefined(opts) };
  const loc = locate(obs, t, o.maxInterpolateGapSec, o.maxExtrapolateSec, (p) => p.speedMps != null && p.headingDeg != null);
  if (!loc) return null;
  const { prev, next } = loc;
  let lat = prev.lat;
  let lon = prev.lon;
  let accuracyM = prev.accuracyM;
  let speedMps = prev.speedMps;
  let headingDeg = prev.headingDeg;
  let altM = prev.altM;
  let quality: CrewState['quality'] = 'observed';
  if (loc.mode === 'interpolate' && next) {
    const p = interpolateGreatCircle(prev, next, loc.f);
    lat = p.lat;
    lon = p.lon;
    accuracyM = lerpN(prev.accuracyM, next.accuracyM, loc.f);
    speedMps = lerpN(prev.speedMps, next.speedMps, loc.f);
    headingDeg = lerpAngle(prev.headingDeg, next.headingDeg, loc.f);
    altM = lerpN(prev.altM, next.altM, loc.f);
    quality = 'interpolated';
  } else if (loc.mode === 'extrapolate') {
    const p = destinationPoint(prev, prev.headingDeg!, prev.speedMps! * loc.elapsedSec);
    lat = p.lat;
    lon = p.lon;
    quality = 'estimated';
  }
  if (loc.nearSample) quality = prev.source === 'manual' ? 'manual' : 'observed';
  else if (loc.mode === 'hold' && prev.source === 'manual') quality = 'manual';

  const ageSec = (t - prev.t) / 1000;
  const toFt = (m: number | null) => (m != null ? m / M_PER_FT : null);
  const current: TrailPoint | null = loc.mode === 'observed-exact' ? null : { t, lat, lon, altFt: toFt(altM) };
  const trail = buildTrail(obs, loc.i, t, o.trailSec, o.trailMaxPoints, (x) => toFt(x.altM), current);
  const state: CrewState = {
    userId,
    t,
    lat,
    lon,
    accuracyM,
    speedMps,
    headingDeg: headingDeg != null ? normalizeDeg(headingDeg) : null,
    quality,
    lastObservedT: prev.t,
    ageSec,
    freshness: freshnessFor(ageSec, CREW_FRESHNESS),
    trail,
  };
  const f = facingBefore(obs, loc.i, t);
  if (f) {
    state.facingDeg = normalizeDeg(f.facingDeg!);
    state.facingAccuracyDeg = f.facingAccuracyDeg ?? null;
    state.facingT = Math.min(t, f.facingT ?? f.t);
  }
  return state;
}

// ───────────── phone facing (round 4) ─────────────

/** A phone facing older than this (s) no longer aims a manned camera ("aim unknown"). */
export const FACING_MAX_AGE_SEC = 10;
/** A phone facing less accurate than ± this (°) no longer aims a manned camera. */
export const FACING_MAX_ACCURACY_DEG = 25;
/** How far back (s) crewStateAt looks for the newest facing (older ones are not reported at all). */
const FACING_LOOKBACK_SEC = 60;

/** Newest crew observation at or before index `i` carrying a facing, within FACING_LOOKBACK_SEC of t. */
function facingBefore(obs: readonly CrewObservation[], i: number, t: EpochMs): CrewObservation | null {
  const floor = t - FACING_LOOKBACK_SEC * 1000;
  for (let j = i; j >= 0; j--) {
    const o = obs[j]!;
    if (o.t < floor) return null;
    if (o.facingDeg != null && Number.isFinite(o.facingDeg)) return o;
  }
  return null;
}

/**
 * The phone facing (°T) that may aim a manned camera at `t`: fresh (≤ FACING_MAX_AGE_SEC) and
 * accurate (≤ FACING_MAX_ACCURACY_DEG, or accuracy not reported). Null otherwise.
 */
export function usableFacing(c: Pick<CrewState, 'facingDeg' | 'facingAccuracyDeg' | 'facingT'> | null | undefined, t: EpochMs): { deg: number; ageSec: number } | null {
  if (!c || c.facingDeg == null || c.facingT == null) return null;
  const ageSec = Math.max(0, (t - c.facingT) / 1000);
  if (ageSec > FACING_MAX_AGE_SEC) return null;
  if (c.facingAccuracyDeg != null && c.facingAccuracyDeg > FACING_MAX_ACCURACY_DEG) return null;
  return { deg: c.facingDeg, ageSec };
}

function stripUndefined<T extends object>(o: T | undefined): Partial<T> {
  if (!o) return {};
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(o)) if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  return out;
}

export interface WorldOptions {
  track?: TrackOptions;
  markerWindowSec?: number; // default 30
  alertWindowSec?: number; // default 120
  /** Historical altitude minima used by callers. They do not establish ground contact for phase inference. */
  baselineAltFt?: Record<string, number | null | undefined>;
  /**
   * Ground elevation (m MSL) lookup; when given, aircraft states get `groundElevM` / `aglFt`. Must be
   * synchronous and cheap (the server passes a sampler over already-cached DEM tiles).
   */
  groundElevation?: (lat: number, lon: number) => number | null;
}

// ───────────── per-array caches (incrementally extended as live buffers grow) ─────────────

interface CameraObsIndex {
  len: number;
  tail: CameraObservation | undefined;
  /** For each i: index of the newest obs ≤ i with that field non-null, or −1. */
  pos: Int32Array;
  heading: Int32Array;
  focal: Int32Array;
  recording: Int32Array;
}

const cameraIndexCache = new WeakMap<readonly CameraObservation[], CameraObsIndex>();

function cameraObsIndex(obs: readonly CameraObservation[]): CameraObsIndex {
  let idx = cameraIndexCache.get(obs);
  const n = obs.length;
  if (idx && idx.len === n && idx.tail === obs[n - 1]) return idx;
  let from = 0;
  if (idx && idx.len < n && idx.len > 0 && obs[idx.len - 1] === idx.tail) from = idx.len;
  const grow = (old: Int32Array | undefined) => {
    const a = new Int32Array(n);
    if (old && from > 0) a.set(old.subarray(0, from));
    return a;
  };
  const next: CameraObsIndex = {
    len: n,
    tail: obs[n - 1],
    pos: grow(from ? idx!.pos : undefined),
    heading: grow(from ? idx!.heading : undefined),
    focal: grow(from ? idx!.focal : undefined),
    recording: grow(from ? idx!.recording : undefined),
  };
  for (let i = from; i < n; i++) {
    const o = obs[i]!;
    const prev = (a: Int32Array) => (i > 0 ? a[i - 1]! : -1);
    next.pos[i] = o.lat != null && o.lon != null ? i : prev(next.pos);
    next.heading[i] = o.headingDeg != null ? i : prev(next.heading);
    next.focal[i] = o.focalLengthMm != null ? i : prev(next.focal);
    next.recording[i] = o.recording != null ? i : prev(next.recording);
  }
  cameraIndexCache.set(obs, next);
  idx = next;
  return idx;
}

interface WeatherIndex {
  len: number;
  tail: WeatherObservation | undefined;
  metar: Map<string, WeatherObservation[]>;
  taf: Map<string, WeatherObservation[]>;
  general: WeatherObservation[];
}

const weatherCache = new WeakMap<readonly WeatherObservation[], WeatherIndex>();

function weatherIndex(weather: readonly WeatherObservation[]): WeatherIndex {
  const n = weather.length;
  const hit = weatherCache.get(weather);
  if (hit && hit.len === n && hit.tail === weather[n - 1]) return hit;
  const idx: WeatherIndex = { len: n, tail: weather[n - 1], metar: new Map(), taf: new Map(), general: [] };
  for (const w of weather) {
    if (w.payload.kind === 'general') idx.general.push(w);
    else {
      const station = w.stationId ?? w.payload.report.stationId;
      const m = w.payload.kind === 'metar' ? idx.metar : idx.taf;
      let list = m.get(station);
      if (!list) m.set(station, (list = []));
      list.push(w);
    }
  }
  const byT = (a: WeatherObservation, b: WeatherObservation) => a.t - b.t || a.receivedAt - b.receivedAt || a.id.localeCompare(b.id);
  for (const l of idx.metar.values()) l.sort(byT);
  for (const l of idx.taf.values()) l.sort(byT);
  idx.general.sort(byT);
  weatherCache.set(weather, idx);
  return idx;
}

/** Latest published observation actually received by this point on the replay timeline. */
function weatherKnownAt(list: readonly WeatherObservation[], t: EpochMs): WeatherObservation | null {
  for (let i = lastIndexAtOrBefore(list, t); i >= 0; i--) {
    const w = list[i]!;
    if (Number.isFinite(w.receivedAt) && w.receivedAt <= t) {
      for (let j = i - 1; j >= 0 && list[j]!.t === w.t && list[j]!.receivedAt === w.receivedAt; j--) {
        if (list[j]!.source !== w.source || JSON.stringify(list[j]!.payload) !== JSON.stringify(w.payload)) return null;
      }
      return w;
    }
  }
  return null;
}

function newestPerStation<R>(map: Map<string, WeatherObservation[]>, t: EpochMs, order: string[]): R[] {
  const found: Array<{ station: string; report: R }> = [];
  for (const [station, list] of map) {
    const observation = weatherKnownAt(list, t);
    if (observation) found.push({ station, report: observation.payload.report as R });
  }
  const rank = (s: string) => {
    const k = order.indexOf(s);
    return k < 0 ? Number.MAX_SAFE_INTEGER : k;
  };
  found.sort((a, b) => rank(a.station) - rank(b.station) || a.station.localeCompare(b.station));
  return found.map((f) => f.report);
}

// ───────────── cameras ─────────────

/**
 * Convenience used by buildWorldState; exported for tests/UI.
 * Position: newest camera observation with lat/lon ≤ t (within 120 s) → crew operator's state
 * (mount 'crew') → aircraft state (mount 'aircraft') → fixed position ('static' for static
 * mounts, otherwise 'manual') → none.
 * Heading/focal/recording: newest observation carrying the field ≤ t, else the camera config.
 */
export function cameraStatesAt(record: MissionRecord, t: EpochMs, aircraft: AircraftState[], crew: CrewState[]): CameraState[] {
  const out: CameraState[] = [];
  for (const cam of record.cameras) {
    const obs = record.cameraObs[cam.id] ?? [];
    const i = obs.length ? lastIndexAtOrBefore(obs, t) : -1;
    let position: LatLon | null = null;
    let positionSource: CameraState['positionSource'] = 'none';
    let freshness: Freshness = 'none';
    let headingDeg = cam.headingDeg;
    let focalLengthMm = cam.focalLengthMm;
    let recording = cam.recording;

    if (i >= 0) {
      const idx = cameraObsIndex(obs);
      const pi = idx.pos[i]!;
      if (pi >= 0) {
        const po = obs[pi]!;
        const age = (t - po.t) / 1000;
        if (age <= CAMERA_POSITION_MAX_AGE_SEC) {
          position = { lat: po.lat!, lon: po.lon! };
          positionSource = 'observation';
          freshness = freshnessFor(age, CREW_FRESHNESS);
        }
      }
      const hi = idx.heading[i]!;
      if (hi >= 0) headingDeg = obs[hi]!.headingDeg;
      const fi = idx.focal[i]!;
      if (fi >= 0) focalLengthMm = obs[fi]!.focalLengthMm;
      const ri = idx.recording[i]!;
      if (ri >= 0) recording = obs[ri]!.recording!;
    }

    // Manned: aimed by a fresh camera-reported heading, else the operator's phone facing; a
    // configured heading means nothing on a camera that moves with a person ("aim unknown").
    let aim: CameraState['aim'] = headingDeg != null ? 'set' : undefined;
    let aimAgeSec: number | null = null;
    const op = cam.mount === 'crew' && cam.operatorUserId ? crew.find((c) => c.userId === cam.operatorUserId) : undefined;
    if (cam.mount === 'crew') {
      headingDeg = null;
      aim = 'unknown';
      const hi = i >= 0 ? cameraObsIndex(obs).heading[i]! : -1;
      const hAge = hi >= 0 ? (t - obs[hi]!.t) / 1000 : Infinity;
      const facing = usableFacing(op, t);
      if (hAge <= FACING_MAX_AGE_SEC) {
        headingDeg = obs[hi]!.headingDeg;
        aim = 'live';
        aimAgeSec = hAge;
      } else if (facing) {
        headingDeg = facing.deg;
        aim = 'live';
        aimAgeSec = facing.ageSec;
      }
    }
    if (!position && op) {
      position = { lat: op.lat, lon: op.lon };
      positionSource = 'operator';
      freshness = op.freshness;
    }
    if (!position && cam.mount === 'aircraft' && cam.aircraftId) {
      const as = aircraft.find((a) => a.aircraftId === cam.aircraftId);
      if (as) {
        position = { lat: as.lat, lon: as.lon };
        positionSource = 'aircraft';
        freshness = as.freshness;
      }
    }
    if (!position && cam.position) {
      position = { lat: cam.position.lat, lon: cam.position.lon };
      positionSource = cam.mount === 'static' ? 'static' : 'manual';
      freshness = 'live';
    }

    const fov = cameraFov({ sensorWidthMm: cam.sensorWidthMm, sensorHeightMm: cam.sensorHeightMm, focalLengthMm });
    const state: CameraState = {
      cameraId: cam.id,
      t,
      position,
      positionSource,
      headingDeg: headingDeg != null ? normalizeDeg(headingDeg) : null,
      focalLengthMm,
      hfovDeg: fov ? fov.hfovDeg : null,
      recording,
      freshness,
    };
    if (aim) state.aim = aim;
    if (aimAgeSec != null) state.aimAgeSec = aimAgeSec;
    const range = isZoom(cam) ? focalRange(cam) : null;
    const sw = cam.sensorWidthMm;
    if (range && sw != null && sw > 0) {
      state.hfovWideDeg = fovDeg(sw, range.minMm);
      state.hfovTeleDeg = fovDeg(sw, range.maxMm);
    }
    out.push(state);
  }
  return out;
}

// ───────────── flight phase ─────────────

interface AltIndex {
  len: number;
  tail: AircraftObservation | undefined;
  /** Running minimum altitude (ft) up to and including index i; NaN when none yet. */
  minAlt: Float64Array;
}

const altIndexCache = new WeakMap<readonly AircraftObservation[], AltIndex>();

function altIndex(obs: readonly AircraftObservation[]): AltIndex {
  const n = obs.length;
  const hit = altIndexCache.get(obs);
  if (hit && hit.len === n && hit.tail === obs[n - 1]) return hit;
  let from = 0;
  const minAlt = new Float64Array(n);
  if (hit && hit.len < n && hit.len > 0 && obs[hit.len - 1] === hit.tail) {
    minAlt.set(hit.minAlt.subarray(0, hit.len));
    from = hit.len;
  }
  for (let i = from; i < n; i++) {
    const prev = i > 0 ? minAlt[i - 1]! : NaN;
    const a = obs[i]!.altFt;
    minAlt[i] = a == null ? prev : Number.isNaN(prev) ? a : Math.min(prev, a);
  }
  const idx: AltIndex = { len: n, tail: obs[n - 1], minAlt };
  altIndexCache.set(obs, idx);
  return idx;
}

/** Lowest recorded altitude at or before `t`; a historical floor, not evidence of ground elevation. */
export function baselineAltitudeFt(obs: readonly AircraftObservation[], t: EpochMs): number | null {
  const i = lastIndexAtOrBefore(obs, t);
  if (i < 0) return null;
  const v = altIndex(obs).minAlt[i]!;
  return Number.isNaN(v) ? null : v;
}

/** Airports within this distance of an aircraft cap its ground baseline at their elevation. */
export const BASELINE_AIRPORT_RADIUS_M = 5 * 1852;

/** Compatible local ground reference; historical track minima never establish height. */
export function localGroundAltitudeFt(
  state: LatLon & { altRef?: AltitudeRef | null },
  airports: ReadonlyArray<{ lat: number; lon: number; elevFt: number }> = [],
  terrainAltFt?: number | null,
): number | null {
  if (state.altRef != null && state.altRef !== 'MSL') return null;
  if (terrainAltFt != null && Number.isFinite(terrainAltFt)) return terrainAltFt;
  let ground: number | null = null;
  let nearestM = BASELINE_AIRPORT_RADIUS_M;
  for (const ap of airports) {
    if (!Number.isFinite(ap.elevFt)) continue;
    const d = distanceM(state, ap);
    if (d < nearestM) { nearestM = d; ground = ap.elevFt; }
  }
  return ground;
}

/**
 * Flight phase uses local terrain, then the nearest airport within 5 NM. Historical track minima
 * are not ground evidence. Airport/terrain elevations are MSL and cannot classify raw PA/HAE.
 * Reported telemetry wins only while the source is usable; old projected states cannot land.
 */
export function phaseForState(
  state: AircraftState,
  obs: readonly AircraftObservation[],
  opts: { baselineAltFt?: number | null; groundAltFt?: number | null; airports?: ReadonlyArray<{ lat: number; lon: number; elevFt: number }> } = {},
): PhaseResult {
  const unknown: PhaseResult = { phase: 'UNKNOWN', estimated: true };
  const i = lastIndexAtOrBefore(obs, state.t);
  if (i < 0 || state.freshness === 'stale' || state.freshness === 'lost' || state.freshness === 'none') return unknown;
  const newest = obs[i]!;
  if (newest.phase && state.t - newest.t <= aircraftFreshnessFor(newest.source).aging * 1000) return { phase: newest.phase, estimated: false };
  const ref = state.altRef ?? 'MSL';
  const next = obs[i + 1];
  if (next && next.t > state.t && state.quality === 'interpolated' && (next.altRef ?? 'MSL') !== ref) return unknown;
  const from = lastIndexAtOrBefore(obs, state.t - PHASE_TREND_SEC * 1000);
  const samples = obs.slice(Math.max(0, from), i + 1).filter((o) => (o.altRef ?? 'MSL') === ref);
  const ground = localGroundAltitudeFt(state, opts.airports, opts.groundAltFt);
  return inferFlightPhase(state, { baselineAltFt: ground, samples });
}

// ───────────── world ─────────────

/**
 * Reconstruct the complete world at `t` from a MissionRecord.
 * Camera positions resolve in order: newest camera observation with lat/lon ≤ t (within 120 s) →
 * crew operator's state (mount 'crew') → aircraft state (mount 'aircraft') → fixed position.
 * Sun is computed at mission.center.
 *
 * Output ordering: aircraft/crew/cameras follow record order; METARs/TAFs follow record.airports
 * order then station id; activeMedia by startT; nearbyMarkers by t; recentAlerts newest first.
 * Telemetry uses binary search + cached per-array indexes, so this is cheap enough to call every frame.
 */
export function buildWorldState(record: MissionRecord, t: EpochMs, opts?: WorldOptions): WorldState {
  const markerWindowMs = (opts?.markerWindowSec ?? 30) * 1000;
  const alertWindowMs = (opts?.alertWindowSec ?? 120) * 1000;

  const aircraft: AircraftState[] = [];
  for (const a of record.aircraft) {
    const obs = record.aircraftObs[a.id] ?? [];
    const s = aircraftStateAt(a.id, obs, t, opts?.track);
    if (s) {
      let groundFt: number | null = null;
      if (opts?.groundElevation) {
        const g = opts.groundElevation(s.lat, s.lon);
        s.groundElevM = g;
        s.aglFt = g == null || !Number.isFinite(g) || s.altFt == null || (s.altRef != null && s.altRef !== 'MSL') ? null : Math.round(s.altFt - g / M_PER_FT);
        groundFt = g == null ? null : g / M_PER_FT;
      }
      // Local ground overrides historical minima; an old sea-level sample must not mask a
      // landing on a higher field. Only compatible altitude references can establish height.
      const ph = phaseForState(s, obs, { groundAltFt: groundFt, airports: record.airports });
      s.phase = ph.phase;
      s.phaseEstimated = ph.estimated;
      aircraft.push(s);
    }
  }
  const crew: CrewState[] = [];
  const seen = new Set<string>();
  for (const m of record.members) {
    if (seen.has(m.userId)) continue;
    seen.add(m.userId);
    const s = crewStateAt(m.userId, record.crewObs[m.userId] ?? [], t, opts?.track ? { ...opts.track, maxExtrapolateSec: 0 } : undefined);
    if (s) crew.push(s);
  }
  const cameras = cameraStatesAt(record, t, aircraft, crew);

  const c = record.mission.center;
  const sp = sunPosition(t, c.lat, c.lon);

  const wx = weatherIndex(record.weather);
  const order = record.airports.map((a) => a.icao);
  const metars = newestPerStation<MetarReport>(wx.metar, t, order);
  const tafs = newestPerStation<TafReport>(wx.taf, t, order);
  const generalObservation = weatherKnownAt(wx.general, t);
  const general = generalObservation ? generalObservation.payload.report as GeneralWeather : null;

  const activeMedia = record.mediaClips.filter((m) => m.startT <= t && t <= m.endT).sort((a, b) => a.startT - b.startT);
  const nearbyMarkers = record.markers.filter((m) => Math.abs(m.t - t) <= markerWindowMs).sort((a, b) => a.t - b.t);
  const recentAlerts = record.alerts.filter((a) => a.t <= t && a.t >= t - alertWindowMs).sort((a, b) => b.t - a.t);

  return {
    missionId: record.mission.id,
    t,
    aircraft,
    crew,
    cameras,
    sun: { t, azimuthDeg: sp.azimuthDeg, elevationDeg: sp.elevationDeg, phase: lightPhase(sp.elevationDeg) },
    metars,
    tafs,
    general,
    activeMedia,
    nearbyMarkers,
    recentAlerts,
  };
}
